import { useCallback, useEffect, useRef, useState } from "react";
// TODO: find alternative using latest APIs
import { useHistory } from "react-router-dom";

import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
// TODO: replace with marked
import { Remarkable } from "remarkable";
import styled from "styled-components/macro";

import { YoutubeLinkT, parseVideoId } from "./LinkParser";
import PostDate from "./PostDate";

interface PostPreviewProps {
  title: string;
  date: string;
  content: string;
  id: string | number;
  isModalView: boolean;
  onPostClick: (() => void) | null;
  isActive?: boolean;
}

function PostPreview({
  title,
  date,
  content,
  id,
  isModalView,
  onPostClick,
  isActive = false
}: PostPreviewProps): JSX.Element {
  const history = useHistory();

  // state
  const [youtubeLink, setYoutubeLink] = useState<YoutubeLinkT | null>(null);
  const [body, setBody] = useState(null);
  const [activePost, setActivePost] = useState(false);

  const md = useRef(new Remarkable());

  const getMarkup = useCallback(() => {
    // Reset youtube link state at the start of each parse
    setYoutubeLink(null);

    // Match all markdown links but exclude image links (which start with !)
    // This regex matches: [text](url) but NOT ![text](url)
    const links = /(?<!!)\[([^\]]*)\]\(([^)]+)\)/g[Symbol.match](content);
    let modifiedContent = content;
    if (links !== null) {
      for (const linkMatch of links) {
        // Extract just the URL part from the match
        const urlMatch = linkMatch.match(/\[([^\]]*)\]\(([^)]+)\)/);
        if (urlMatch) {
          const url = urlMatch[2]; // The URL is the second capture group
          const video = parseVideoId(url);

          if (video) {
            setYoutubeLink(video);
            const toReplace = new RegExp(
              `\\[(.*?)\\]\\(\\b${url.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")}\\b\\)`
            );
            modifiedContent = content.replace(toReplace, "");
            break;
          }
        }
      }
    }
    return { __html: md.current.render(modifiedContent) };
  }, [content, md]);

  useEffect(() => setBody(getMarkup()), [getMarkup]);

  function openPost() {
    if (onPostClick) {
      // Use custom click handler for modal context
      onPostClick();
    }
  }

  function openDocInNewTab() {
    if (isModalView) {
      window.open("/user-documentation-post/" + id);
    } else {
      history.push("/user-documentation-post/" + id);
    }
  }

  useEffect(() => {
    setActivePost(isActive);
  }, [isActive]);

  return (
    <>
      <Wrapper
        className={[!isModalView && "newTabWrapper", activePost && "activePost"]}
        onClick={isModalView ? openPost : openDocInNewTab}
        data-testid="post-preview">
        <PostTitle>{title}</PostTitle>
        <PostDate value={date} />
        {Boolean(youtubeLink) && (
          <PreviewVideo>
            <PlayCircleFilledIcon fontSize="inherit" />
            <p className="label">Video Content</p>
          </PreviewVideo>
        )}
        <PostContent dangerouslySetInnerHTML={body} />

        <ReadMoreWrapper>
          {isModalView && (
            <ReadMoreButton onClick={openDocInNewTab}>
              Read More
              {isModalView ? <ArrowForwardIcon fontSize="large" /> : null}
            </ReadMoreButton>
          )}
        </ReadMoreWrapper>
      </Wrapper>
    </>
  );
}

export default PostPreview;

const ReadMoreButton = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-40);
  transition: color 0.2s ease-in-out;
  cursor: pointer;
  width: fit-content;

  & > svg {
    transition: translate 0.2s ease-in-out;
    rotate: -45deg;
  }

  &:hover {
    color: var(--color-text-80);

    svg {
      translate: 3px -3px;
    }
  }
`;

const PostTitle = styled.h2`
  color: var(--color-primary);
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 8px;
`;

const PostContent = styled.div`
  word-wrap: break-word;
  overflow: hidden;
  min-height: 36px;
  max-height: 120px;

  & > * {
    color: var(--color-text-80);
  }

  a,
  .link {
    color: #1890ff;
    text-decoration: underline;
  }

  h1,
  h2,
  h3 {
    font-size: 16px;
    font-weight: bold;
  }
`;

const ReadMoreWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    0deg,
    rgba(var(--gradient-color) / 100%) 60%,
    rgba(var(--gradient-color) / 0%) 100%
  );
  padding: 20px 20px 16px;
  border-radius: var(--border-radius);
  min-height: 58px;
`;

const PreviewVideo = styled.div`
  grid-area: body;
  font-size: 16px;
  color: var(--color-text-60);
  transition: color var(--duration);
  display: flex;
  place-self: center;
  align-items: center;
  z-index: 1;

  .label {
    padding-left: 5px;
    color: var(--color-text-80);
  }
`;

const Wrapper = styled.div`
  --gradient-color: 255 255 255;
  background-color: white;
  border: 1px solid var(--color-text-10);

  display: flex;
  flex-direction: column;
  padding: 16px 20px 0;
  cursor: pointer;
  border-radius: var(--border-radius);

  &.newTabWrapper {
    border-radius: 0;
  }

  &.activePost {
    --gradient-color: 204 244 239;
    background-color: #ccf4ef;
    border: 1px solid var(--color-primary);

    ${PostTitle} {
      color: var(--color-text);
    }
  }

  &:hover {
    --gradient-color: 244 245 245;
    background-color: #f4f5f5; // color-text-06 no alpha
    border: 1px solid #f4f5f5;

    &.activePost {
      --gradient-color: 204 244 239;
      background-color: #ccf4ef;
      border: 1px solid var(--color-primary);
    }

    ${PostContent} > * {
      color: var(--color-text);
    }

    ${PreviewVideo} {
      color: var(--color-text-80);

      .label {
        color: var(--color-text);
      }
    }
  }
`;
